import { API_SECRET, USER_MODULE_USAGE_URL } from "../config/appConfig";
import { getDeviceId } from "./deviceStorage";
import { awaitPostUpdateHandoff } from "../sync/apiClient";
import { traceAndFetch } from "../sync/apiTrace"; // v3.2.08

export const WARM_USAGE_STORAGE_KEY = "fz:warmUsage";
export const WARM_USAGE_PENDING_KEY = "fz:warmUsagePending";
export const SESSION_WARM_STORAGE_KEY = "fz:warmSessionUsage";
export const WARM_CONTROL_STORAGE_KEY = "fz:warmControlState";

let warmUsageLoadInFlight = null;
let warmUsageFlushInFlight = null;
let warmUsageLoadLastAt = 0;
let warmUsageFlushLastAt = 0;
const WARM_USAGE_LOAD_MIN_INTERVAL_MS = 120000;
const WARM_USAGE_FLUSH_MIN_INTERVAL_MS = 120000;

export function normalizeWarmControlState(input) {
  const source = input && typeof input === "object" ? input : {};
  const runsSource = source.runs && typeof source.runs === "object" ? source.runs : {};
  const runs = {};
  Object.entries(runsSource).forEach(([key, value]) => {
    const cleanKey = String(key || "").trim();
    if (!cleanKey) return;
    const ts = Math.max(0, Number(value?.ts || 0));
    const count = Math.max(0, Number(value?.count || 0));
    if (!ts && !count) return;
    runs[cleanKey] = { ts, count };
  });
  return { runs };
}

export function readWarmControlState() {
  try {
    if (typeof sessionStorage === "undefined") return { runs: {} };
    return normalizeWarmControlState(JSON.parse(sessionStorage.getItem(WARM_CONTROL_STORAGE_KEY) || "{}"));
  } catch (e) {
    return { runs: {} };
  }
}

export function writeWarmControlState(value) {
  try {
    if (typeof sessionStorage === "undefined") return;
    sessionStorage.setItem(WARM_CONTROL_STORAGE_KEY, JSON.stringify(normalizeWarmControlState(value)));
  } catch (e) {}
}

export function getWarmConnectionProfile() {
  try {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;
    const effectiveType = String(conn?.effectiveType || "").toLowerCase();
    const saveData = !!conn?.saveData;
    const deviceMemory = Math.max(0, Number(navigator.deviceMemory || 0));
    const cores = Math.max(0, Number(navigator.hardwareConcurrency || 0));
    const hidden = typeof document !== "undefined" && document.visibilityState === "hidden";
    const constrained = saveData || effectiveType === "slow-2g" || effectiveType === "2g" || (!!deviceMemory && deviceMemory <= 2) || (!!cores && cores <= 2);

    return {
      effectiveType,
      saveData,
      deviceMemory,
      cores,
      hidden,
      constrained,
      moduleBudget: constrained ? 2 : (effectiveType === "3g" || (!!deviceMemory && deviceMemory <= 4) ? 3 : 5),
      cooldownMs: constrained ? 180000 : (effectiveType === "3g" ? 120000 : 75000),
      freshnessFactor: constrained ? 1.8 : (effectiveType === "3g" ? 1.35 : 1),
      requestCacheOnly: saveData || effectiveType === "slow-2g" || effectiveType === "2g",
    };
  } catch (e) {
    return {
      effectiveType: "",
      saveData: false,
      deviceMemory: 0,
      cores: 0,
      hidden: false,
      constrained: false,
      moduleBudget: 4,
      cooldownMs: 90000,
      freshnessFactor: 1,
      requestCacheOnly: false,
    };
  }
}

export function shouldRunControlledWarm(runKey, minIntervalMs = 90000) {
  try {
    const now = Date.now();
    const state = readWarmControlState();
    const entry = state.runs?.[runKey];
    if (entry?.ts && (now - Number(entry.ts || 0)) < Math.max(15000, Number(minIntervalMs || 90000))) {
      return false;
    }
    state.runs = state.runs || {};
    state.runs[runKey] = {
      ts: now,
      count: Math.min(999, Number(entry?.count || 0) + 1),
    };
    writeWarmControlState(state);
    return true;
  } catch (e) {
    return true;
  }
}

export function warmUsageModuleKey(rawKey) {
  return String(rawKey || "").trim();
}

export function warmUsageBaseModuleKey(rawKey) {
  const clean = String(rawKey || "").trim();
  if (!clean) return "";
  const parts = clean.split(":").filter(Boolean);
  if (!parts.length) return "";
  if ((parts[0] === "farm" || parts[0] === "home") && parts.length >= 2) {
    return String(parts[parts.length - 1] || "").trim();
  }
  return String(parts[0] || "").trim();
}

export const MODULE_PRIORITY_WARM_PLAN = Object.freeze({
  purchases: { farm: ["orders", "notifications"], global: ["people", "suppliers", "stock", "adminGlobalsConfig"] },
  stock: { farm: ["stockMovements", "notifications"], global: ["stock", "adminGlobalsConfig"] },
  services: { farm: ["services", "occurrences", "notifications"], global: ["people", "providers", "serviceTypes", "stock", "adminGlobalsConfig"] },
  services_external: { farm: ["services", "notifications"], global: ["people", "providers", "serviceTypes", "adminGlobalsConfig"] },
  occurrences: { farm: ["occurrences", "notifications"], global: ["people", "serviceTypes", "adminGlobalsConfig"] },
  inspections: { farm: ["inspections", "notifications"], global: ["adminGlobalsConfig"] },
  financial: { farm: ["financial", "notifications"], global: ["adminGlobalsConfig"] },
  machinery: { farm: ["machinery", "machineryMaintenance", "fueling"], global: ["adminGlobalsConfig"] },
  haras: { farm: ["harasEquines", "harasStalls", "harasSchedule", "harasSettings"], global: ["adminGlobalsConfig"] },
  animals: { farm: ["animals", "animalLots", "animalSettings"], global: ["stock", "adminGlobalsConfig"] },
  pastures: { farm: ["pastures", "poi", "maps"], global: ["adminGlobalsConfig"] },
  poi: { farm: ["poi", "pastures", "maps"], global: ["adminGlobalsConfig"] },
  hr: { farm: ["employees", "notifications"], global: ["adminGlobalsConfig"] },
  meu_perfil: { farm: ["notifications"], global: ["adminGlobalsConfig"] },
});

export function getControlledWarmModuleSlice(moduleIds, { scope = "priority", maxModules = null } = {}) {
  const profile = getWarmConnectionProfile();
  const ordered = [];
  const seen = new Set();
  (Array.isArray(moduleIds) ? moduleIds : []).forEach((moduleId) => {
    const clean = warmUsageBaseModuleKey(moduleId);
    if (!clean || seen.has(clean) || !MODULE_PRIORITY_WARM_PLAN[clean]) return;
    seen.add(clean);
    ordered.push(clean);
  });
  const hardLimit = Math.max(1, Number(maxModules || (
    scope === "adaptive" ? profile.moduleBudget + 1 :
    scope === "session" ? Math.max(2, profile.moduleBudget) :
    profile.moduleBudget
  )));
  return ordered.slice(0, hardLimit);
}

export async function runControlledPriorityWarm({
  scope = "priority",
  reason = "warm",
  farmId = "",
  userName = "",
  moduleIds = [],
  freshnessMs = 90000,
  currentModule = "",
  maxModules = null,
} = {}, deps = {}) {
  const { requestWarmScope } = deps;
  const profile = getWarmConnectionProfile();
  if (profile.hidden && scope !== "shell" && scope !== "home") return;
  const selectedModules = getControlledWarmModuleSlice(moduleIds, { scope, maxModules });
  const targetFarmId = String(farmId || "").trim();
  const baseCurrent = warmUsageBaseModuleKey(currentModule);
  const runKey = ["warm", scope, targetFarmId || "global", baseCurrent || "none", selectedModules.join(",")].join("|");
  const minIntervalMs = Math.max(20000, Math.round(profile.cooldownMs * (
    scope === "adaptive" ? 1.3 :
    scope === "session" ? 0.85 :
    1
  )));
  if (!shouldRunControlledWarm(runKey, minIntervalMs)) return;

  await Promise.resolve(requestWarmScope?.(scope, reason)).catch(() => {});
  if (!targetFarmId || !selectedModules.length || profile.requestCacheOnly) return;

  const adjustedFreshnessMs = Math.max(60000, Math.round(Number(freshnessMs || 90000) * Number(profile.freshnessFactor || 1)));
  await warmPriorityCollections({
    farmId: targetFarmId,
    userName: userName || `${scope}-warm`,
    moduleIds: selectedModules,
    freshnessMs: adjustedFreshnessMs,
  }, deps).catch(() => {});
}

export function normalizeWarmUsageMap(input) {
  const source = input && typeof input === "object" ? input : {};
  const next = {};
  Object.entries(source).forEach(([key, value]) => {
    const cleanKey = String(key || "").trim();
    if (!cleanKey) return;
    const hits = Math.max(0, Number(value?.hits || 0));
    const ts = Math.max(0, Number(value?.ts || 0));
    if (!hits && !ts) return;
    next[cleanKey] = { hits, ts };
  });
  return next;
}

export function readWarmUsageMap(storageKey = WARM_USAGE_STORAGE_KEY) {
  try {
    if (typeof localStorage === "undefined") return {};
    return normalizeWarmUsageMap(JSON.parse(localStorage.getItem(storageKey) || "{}"));
  } catch (e) {
    return {};
  }
}

export function writeWarmUsageMap(storageKey, value) {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(storageKey, JSON.stringify(normalizeWarmUsageMap(value)));
  } catch (e) {}
}

export function normalizeSessionWarmState(input) {
  const source = input && typeof input === "object" ? input : {};
  const normalizeList = (list) => (Array.isArray(list) ? list : []).map((item) => ({
    moduleId: String(item?.moduleId || "").trim(),
    ts: Math.max(0, Number(item?.ts || 0)),
  })).filter((item) => item.moduleId).slice(0, 12);

  const normalizeTransitions = (map) => {
    const sourceMap = map && typeof map === "object" ? map : {};
    const next = {};
    Object.entries(sourceMap).forEach(([key, value]) => {
      const cleanKey = String(key || "").trim();
      if (!cleanKey) return;
      const count = Math.max(0, Number(value?.count || 0));
      const ts = Math.max(0, Number(value?.ts || 0));
      if (!count && !ts) return;
      next[cleanKey] = { count, ts };
    });
    return next;
  };

  const sourceScopes = source.scopes && typeof source.scopes === "object" ? source.scopes : {};
  const scopes = {};
  Object.entries(sourceScopes).forEach(([scopeKey, value]) => {
    const cleanScope = String(scopeKey || "").trim();
    if (!cleanScope) return;
    scopes[cleanScope] = {
      recent: normalizeList(value?.recent),
      transitions: normalizeTransitions(value?.transitions),
      lastModuleId: String(value?.lastModuleId || "").trim(),
      lastTs: Math.max(0, Number(value?.lastTs || 0)),
    };
  });

  return { scopes };
}

export function readSessionWarmState() {
  try {
    if (typeof sessionStorage === "undefined") return { scopes: {} };
    return normalizeSessionWarmState(JSON.parse(sessionStorage.getItem(SESSION_WARM_STORAGE_KEY) || "{}"));
  } catch (e) {
    return { scopes: {} };
  }
}

export function writeSessionWarmState(value) {
  try {
    if (typeof sessionStorage === "undefined") return;
    sessionStorage.setItem(SESSION_WARM_STORAGE_KEY, JSON.stringify(normalizeSessionWarmState(value)));
  } catch (e) {}
}

export function recordSessionWarmNavigation(moduleId, { farmId = "" } = {}) {
  const baseModule = warmUsageBaseModuleKey(moduleId);
  if (!baseModule || !MODULE_PRIORITY_WARM_PLAN[baseModule]) return;
  try {
    const now = Date.now();
    const state = readSessionWarmState();
    const scopeKeys = ["global"];
    if (farmId) scopeKeys.unshift(`farm:${String(farmId).trim()}`);

    scopeKeys.forEach((scopeKey) => {
      const scope = state.scopes[scopeKey] || { recent: [], transitions: {}, lastModuleId: "", lastTs: 0 };
      const lastModuleId = String(scope.lastModuleId || "").trim();
      if (lastModuleId && lastModuleId !== baseModule) {
        const transitionKey = `${lastModuleId}>${baseModule}`;
        const existing = scope.transitions[transitionKey] || { count: 0, ts: 0 };
        scope.transitions[transitionKey] = {
          count: Math.min(99, Number(existing.count || 0) + 1),
          ts: now,
        };
      }
      const recent = Array.isArray(scope.recent) ? scope.recent.filter((item) => item?.moduleId && item.moduleId !== baseModule) : [];
      recent.unshift({ moduleId: baseModule, ts: now });
      scope.recent = recent.slice(0, 8);
      scope.lastModuleId = baseModule;
      scope.lastTs = now;
      state.scopes[scopeKey] = scope;
    });

    writeSessionWarmState(state);
  } catch (e) {}
}

export function getSessionWarmCandidates({ farmId = "", currentModule = "", limitPerScope = 4 } = {}) {
  try {
    const baseCurrent = warmUsageBaseModuleKey(currentModule);
    const state = readSessionWarmState();
    const scopeKeys = [];
    if (farmId) scopeKeys.push(`farm:${String(farmId).trim()}`);
    scopeKeys.push("global");

    const result = [];
    const seen = new Set();

    scopeKeys.forEach((scopeKey) => {
      const scope = state.scopes?.[scopeKey];
      if (!scope) return;
      const scores = new Map();

      const recentList = Array.isArray(scope.recent) ? scope.recent : [];
      recentList.forEach((item, index) => {
        const moduleId = warmUsageBaseModuleKey(item?.moduleId);
        if (!moduleId || moduleId === baseCurrent || !MODULE_PRIORITY_WARM_PLAN[moduleId]) return;
        const score = Math.max(0, 500 - (index * 25)) + Math.max(0, Number(item?.ts || 0) / 1000000000);
        scores.set(moduleId, Math.max(scores.get(moduleId) || 0, score));
      });

      const transitions = scope.transitions && typeof scope.transitions === "object" ? scope.transitions : {};
      Object.entries(transitions).forEach(([key, value]) => {
        const [fromRaw, toRaw] = String(key || "").split(">");
        const cleanFrom = warmUsageBaseModuleKey(fromRaw);
        const cleanTo = warmUsageBaseModuleKey(toRaw);
        if (!cleanFrom || !cleanTo || cleanTo === baseCurrent || !MODULE_PRIORITY_WARM_PLAN[cleanTo]) return;
        if (baseCurrent && cleanFrom !== baseCurrent) return;
        const score = (Math.max(0, Number(value?.count || 0)) * 40) + Math.max(0, Number(value?.ts || 0) / 1000000000);
        scores.set(cleanTo, Math.max(scores.get(cleanTo) || 0, score));
      });

      Array.from(scores.entries())
        .sort((a, b) => b[1] - a[1])
        .map(([moduleId]) => moduleId)
        .slice(0, limitPerScope)
        .forEach((moduleId) => {
          if (seen.has(moduleId)) return;
          seen.add(moduleId);
          result.push(moduleId);
        });
    });

    return result;
  } catch (e) {
    return [];
  }
}

export function buildFarmWarmUsageKey(farmId, moduleId) {
  const fid = String(farmId || "").trim();
  const mid = String(moduleId || "").trim();
  if (!fid || !mid) return "";
  return `farm:${fid}:${mid}`;
}

export function buildHomeWarmUsageKey(moduleId) {
  const mid = String(moduleId || "").trim();
  if (!mid) return "";
  return `home:${mid}`;
}

export function mergeWarmUsageMaps(baseMap, incomingMap) {
  const base = normalizeWarmUsageMap(baseMap);
  const incoming = normalizeWarmUsageMap(incomingMap);
  const next = { ...base };
  Object.entries(incoming).forEach(([key, value]) => {
    const existing = next[key] || { hits: 0, ts: 0 };
    next[key] = {
      hits: Math.max(Number(existing.hits || 0), Number(value.hits || 0)),
      ts: Math.max(Number(existing.ts || 0), Number(value.ts || 0)),
    };
  });
  return next;
}

export function queueWarmUsageForSync(rawKey) {
  try {
    const moduleKey = warmUsageModuleKey(rawKey);
    if (!moduleKey) return;
    const pending = readWarmUsageMap(WARM_USAGE_PENDING_KEY);
    const entry = pending[moduleKey] || { hits: 0, ts: 0 };
    entry.hits = Number(entry.hits || 0) + 1;
    entry.ts = Date.now();
    pending[moduleKey] = entry;
    writeWarmUsageMap(WARM_USAGE_PENDING_KEY, pending);
  } catch (e) {}
}

export function setWarmUsageIdentity(session) {
  try {
    if (typeof window === "undefined") return;
    if (!session?.id && !session?.userId && !session?.username) {
      delete window.__fzWarmUsageIdentity;
      return;
    }
    window.__fzWarmUsageIdentity = {
      accountId: String(session?.id || "").trim(),
      userId: String(session?.userId || "").trim(),
      username: String(session?.username || "").trim(),
      name: String(session?.name || "").trim(),
    };
  } catch (e) {}
}

export async function loadWarmUsageProfile(session) {
  const accountId = String(session?.id || "").trim();
  const userId = String(session?.userId || "").trim();
  const username = String(session?.username || "").trim();
  if (!accountId && !userId && !username) return [];
  const now = Date.now();
  if (warmUsageLoadInFlight) return warmUsageLoadInFlight;
  if ((now - warmUsageLoadLastAt) < WARM_USAGE_LOAD_MIN_INTERVAL_MS) return [];
  warmUsageLoadLastAt = now;
  warmUsageLoadInFlight = (async () => {
  try {
    await awaitPostUpdateHandoff(4200);
    // v3.9.05: não carregar usage durante bootstrap snapshot.
    try {
      const _ex = typeof window !== "undefined" ? window.__fzBootstrapExclusiveMode : null;
      if (_ex?.active && _ex?.phase === "snapshot") {
        try { console.warn("[v3.9.05] user-module-usage load pausado — bootstrap snapshot ativo"); } catch (_) {}
        return [];
      }
    } catch (_) {}
    const res = await traceAndFetch(USER_MODULE_USAGE_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": API_SECRET,
        "X-Device-ID": getDeviceId(),
        "X-User-Name": String(session?.name || session?.username || ""),
      },
      body: JSON.stringify({ action: "list", account_id: accountId, user_id: userId, username }),
    }, { source: "user-module-usage.load", action: "list" });
    const json = await res.json().catch(() => null);
    const items = Array.isArray(json?.data?.items) ? json.data.items : [];
    const incoming = {};
    items.forEach((item) => {
      const moduleKey = warmUsageModuleKey(item?.module_key || "");
      if (!moduleKey) return;
      const hits = Math.max(0, Number(item?.priority_score || item?.usage_count || 0));
      const ts = item?.last_used_at ? new Date(item.last_used_at).getTime() : 0;
      incoming[moduleKey] = { hits, ts: Number.isFinite(ts) ? ts : 0 };
    });
    const merged = mergeWarmUsageMaps(readWarmUsageMap(WARM_USAGE_STORAGE_KEY), incoming);
    writeWarmUsageMap(WARM_USAGE_STORAGE_KEY, merged);
    return items;
  } catch (e) {
    return [];
  } finally {
    warmUsageLoadInFlight = null;
  }
  })();
  return warmUsageLoadInFlight;
}

export async function flushWarmUsageToServer(session, { force = false } = {}) {
  const accountId = String(session?.id || "").trim();
  const userId = String(session?.userId || "").trim();
  const username = String(session?.username || "").trim();
  if (!accountId && !userId && !username) return false;
  if (!force && typeof navigator !== "undefined" && navigator && navigator.onLine === false) return false;

  const pending = readWarmUsageMap(WARM_USAGE_PENDING_KEY);
  const items = Object.entries(pending).map(([moduleKey, value]) => ({
    module_key: String(moduleKey || "").trim(),
    usage_count: Math.max(1, Number(value?.hits || 0)),
    priority_score: Math.max(1, Number(value?.hits || 0)),
    last_used_at: Number(value?.ts || 0) ? new Date(Number(value.ts)).toISOString() : null,
  })).filter((item) => item.module_key);
  if (!items.length) return true;

  const now = Date.now();
  if (warmUsageFlushInFlight) return warmUsageFlushInFlight;
  if (!force && (now - warmUsageFlushLastAt) < WARM_USAGE_FLUSH_MIN_INTERVAL_MS) return false;
  warmUsageFlushLastAt = now;

  warmUsageFlushInFlight = (async () => {
    try {
      await awaitPostUpdateHandoff(4200);
      // v3.9.05: não fazer flush de usage durante bootstrap snapshot.
      try {
        const _ex = typeof window !== "undefined" ? window.__fzBootstrapExclusiveMode : null;
        if (_ex?.active && _ex?.phase === "snapshot") {
          try { console.warn("[v3.9.05] user-module-usage flush pausado — bootstrap snapshot ativo"); } catch (_) {}
          return;
        }
      } catch (_) {}
      const res = await traceAndFetch(USER_MODULE_USAGE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": API_SECRET,
          "X-Device-ID": getDeviceId(),
          "X-User-Name": String(session?.name || session?.username || ""),
        },
        body: JSON.stringify({ action: "upsert", account_id: accountId, user_id: userId, username, items }),
      }, { source: "user-module-usage.flush", action: "upsert" });
      const json = await res.json().catch(() => null);
      if (!res.ok || json?.ok === false) return false;
      writeWarmUsageMap(WARM_USAGE_PENDING_KEY, {});
      return true;
    } catch (e) {
      return false;
    } finally {
      warmUsageFlushInFlight = null;
    }
  })();
  return warmUsageFlushInFlight;
}

export function getWarmUsageEntryInfo(rawKey) {
  const cleanKey = String(rawKey || "").trim();
  if (!cleanKey) return null;
  if (cleanKey.startsWith("signal:")) return null;
  if (cleanKey.endsWith(":priority") || cleanKey.endsWith(":adaptive")) return null;

  const parts = cleanKey.split(":").filter(Boolean);
  if (parts[0] === "farm" && parts.length >= 3) {
    const farmId = String(parts[1] || "").trim();
    const moduleId = String(parts.slice(2).join(":") || "").trim();
    if (!farmId || !MODULE_PRIORITY_WARM_PLAN[moduleId]) return null;
    return { scope: "farm", farmId, moduleId, key: cleanKey };
  }

  if (parts[0] === "home" && parts.length >= 2) {
    const moduleId = String(parts.slice(1).join(":") || "").trim();
    if (!MODULE_PRIORITY_WARM_PLAN[moduleId]) return null;
    return { scope: "home", moduleId, key: cleanKey };
  }

  if (!MODULE_PRIORITY_WARM_PLAN[cleanKey]) return null;
  return { scope: "global", moduleId: cleanKey, key: cleanKey };
}

export function getStoredWarmUsageByScope(options = {}) {
  try {
    const farmId = String(options?.farmId || "").trim();
    const limitPerScope = Math.max(1, Number(options?.limitPerScope || 6));
    const buckets = { farm: new Map(), global: new Map(), home: new Map() };
    const entries = Object.entries(readWarmUsageMap(WARM_USAGE_STORAGE_KEY));

    entries.forEach(([rawKey, value]) => {
      const info = getWarmUsageEntryInfo(rawKey);
      if (!info) return;
      if (info.scope === "farm" && farmId && info.farmId !== farmId) return;
      if (info.scope === "farm" && !farmId) return;

      const score = (Math.max(0, Number(value?.hits || 0)) * 1000) + Math.max(0, Number(value?.ts || 0));
      const bucket = buckets[info.scope];
      const existing = bucket.get(info.moduleId);
      if (!existing || score > existing.score) {
        bucket.set(info.moduleId, {
          moduleId: info.moduleId,
          score,
          hits: Math.max(0, Number(value?.hits || 0)),
          ts: Math.max(0, Number(value?.ts || 0)),
        });
      }
    });

    const sortBucket = (bucket) => Array.from(bucket.values())
      .sort((a, b) => (b.score - a.score) || (b.hits - a.hits) || (b.ts - a.ts))
      .map((item) => item.moduleId)
      .slice(0, limitPerScope);

    return {
      farm: sortBucket(buckets.farm),
      global: sortBucket(buckets.global),
      home: sortBucket(buckets.home),
    };
  } catch (e) {
    return { farm: [], global: [], home: [] };
  }
}

export function buildWarmModuleSequence({ currentModule = null, shortcutModules = [], contextModules = [], fallbackModules = [], farmId = "" } = {}) {
  const ordered = [];
  const push = (value) => {
    const key = String(value || "").trim();
    if (!key || ordered.includes(key) || !MODULE_PRIORITY_WARM_PLAN[key]) return;
    ordered.push(key);
  };

  const scopedUsage = getStoredWarmUsageByScope({ farmId, limitPerScope: 8 });
  const sessionCandidates = getSessionWarmCandidates({ farmId, currentModule, limitPerScope: 4 });

  push(currentModule);
  sessionCandidates.forEach(push);
  scopedUsage.farm.forEach(push);
  scopedUsage.global.forEach(push);
  (Array.isArray(contextModules) ? contextModules : []).forEach(push);
  (Array.isArray(shortcutModules) ? shortcutModules : []).forEach(push);
  scopedUsage.home.forEach(push);
  (Array.isArray(fallbackModules) ? fallbackModules : []).forEach(push);

  return ordered;
}

export async function warmPriorityCollections({ farmId, userName, moduleIds = [], freshnessMs = 90000 } = {}, deps = {}) {
  const {
    syncGlobalCollections,
    getSyncGlobalDeps,
    DB,
    opsKey,
    pendingKey,
    pullCollection,
    mergeBootstrapVisibleWithServer,
    setLiveCollectionCache,
    shadowKey,
    verKey,
    cursorKey,
    windowObj,
  } = deps;

  if (!farmId || !Array.isArray(moduleIds) || !moduleIds.length) return;
  const globalCollections = Array.from(new Set(moduleIds.flatMap((moduleId) => MODULE_PRIORITY_WARM_PLAN[moduleId]?.global || []))).filter(Boolean);
  if (globalCollections.length && typeof syncGlobalCollections === "function") {
    await syncGlobalCollections(userName || "priority-warm", {
      collections: globalCollections,
      freshnessMs,
      skipFresh: true,
    }, typeof getSyncGlobalDeps === "function" ? getSyncGlobalDeps() : {});
  }
  const farmCollections = Array.from(new Set(moduleIds.flatMap((moduleId) => MODULE_PRIORITY_WARM_PLAN[moduleId]?.farm || []))).filter(Boolean);
  for (const col of farmCollections) {
    const localKey = `fz:${farmId}:${col}`;
    try {
      const warmedAt = Number((await DB.get(`${localKey}:_warmAt`)) || 0);
      if (warmedAt && (Date.now() - warmedAt) < freshnessMs) continue;
      const opKey = opsKey(farmId, col);
      const pendKey = pendingKey(farmId, col);
      const pendingOps = (await DB.get(opKey)) || [];
      const pendingCount = (await DB.get(pendKey)) || 0;
      if ((Array.isArray(pendingOps) && pendingOps.length > 0) || pendingCount > 0) continue;
      const remote = await pullCollection(farmId, col, userName || "priority-warm", { forceFull: false }).catch(() => null);
      const remoteData = Array.isArray(remote?.data) ? remote.data : null;
      if (!remoteData) continue;
      const localData = await DB.get(localKey);
      const localArr = Array.isArray(localData) ? localData : [];
      const merged = mergeBootstrapVisibleWithServer(localArr, remoteData, col);
      await DB.set(localKey, merged.raw);
      setLiveCollectionCache(localKey, merged.raw);
      await DB.set(shadowKey(farmId, col), remoteData);
      setLiveCollectionCache(shadowKey(farmId, col), remoteData);
      if (typeof remote?.version === "number") await DB.set(verKey(farmId, col), remote.version || 0);
      if (Object.prototype.hasOwnProperty.call(remote || {}, "cursor")) await DB.set(cursorKey(farmId, col), remote?.cursor || null);
      await DB.set(`${localKey}:_warmAt`, Date.now());
      windowObj?.dispatchEvent?.(new CustomEvent("fz:sync:updated", { detail: { key: localKey } }));
      await new Promise((resolve) => setTimeout(resolve, 0));
    } catch (e) {}
  }
}

export function trackWarmUsage(key) {
  try {
    const storageKey = WARM_USAGE_STORAGE_KEY;
    const normalizedKey = String(key || "").trim();
    if (!normalizedKey) return;
    const next = readWarmUsageMap(storageKey);
    const entry = next[normalizedKey] && typeof next[normalizedKey] === "object" ? next[normalizedKey] : { hits: 0, ts: 0 };
    entry.hits = Number(entry.hits || 0) + 1;
    entry.ts = Date.now();
    next[normalizedKey] = entry;
    writeWarmUsageMap(storageKey, next);
    queueWarmUsageForSync(normalizedKey);
    try {
      if (typeof window !== "undefined") window.__fzFlushWarmUsage?.();
    } catch (e) {}
  } catch (e) {}
}

export function trackContextualWarmUsage(moduleId, { farmId = "", includeHome = false } = {}) {
  const baseModule = warmUsageBaseModuleKey(moduleId);
  if (!baseModule) return;
  trackWarmUsage(baseModule);
  if (includeHome) trackWarmUsage(buildHomeWarmUsageKey(baseModule));
  if (farmId) trackWarmUsage(buildFarmWarmUsageKey(farmId, baseModule));
}
